import * as Sentry from "@sentry/nextjs";
import { isSSR } from "@/lib/utils/isSSR";

type SentryError = Error | string | null | undefined;

interface SentryErrorOptions {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: "error" | "warning" | "info" | "debug";
}

const notify = (
  error: SentryError,
  options: SentryErrorOptions = {}
): string | undefined => {
  const { tags = {}, extra = {}, level = "error" } = options;

  const section = tags.section || "unknown";
  const functionName = tags.function || "unknown";

  if (isSSR) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : error !== null && error !== undefined
        ? String(error)
        : "Unknown error";
    const logFn =
      level === "warning"
        ? console.warn
        : level === "info"
        ? console.info
        : console.error;
    logFn(`[${section}:${functionName}]`, errorMessage);
  }

  try {
    if (error instanceof Error) {
      return Sentry.captureException(error, {
        level,
        tags,
        extra,
      });
    }

    if (typeof error === "string") {
      return Sentry.captureMessage(error, {
        level,
        tags,
        extra,
      });
    }

    if (error !== null && error !== undefined) {
      return Sentry.captureException(error, {
        level,
        tags,
        extra,
      });
    }
  } catch (sentryError) {
    if (isSSR) {
      console.error("Sentry initialization error:", sentryError);
    }
  }

  return undefined;
};

export default {
  notify,
};
